import { useList } from "src/system/useList.tsx";
import type { DefaultError, UseQueryOptions } from "@tanstack/react-query";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import type {
  GridFilterModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { GridSortModel } from "@mui/x-data-grid/models";

import type {
  SelectOption,
  SelectSearchField,
} from "src/components/Form/Form.types.ts";
import type {
  SelectLabelProp,
  SelectValueProp,
} from "src/system/system.types.ts";
import { useSelectOptions } from "src/system/useSelectOptions.ts";
import { getResourceBySelectValue } from "src/system/getResourceBySelectValue.ts";
import { getSelectFilter } from "./getSelectFilter";
import { getSelectSort } from "src/system/getSelectSort.ts";

type UseSelectProps<Resource extends GridValidRowModel = GridValidRowModel> = {
  resource: string;
  labelProp?: SelectLabelProp<Resource>;
  search?: string;
  locale?: string;
  include?: string[];
  useSimplePagination?: boolean;
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  onQueryFn?: () => void;
  valueProp?: SelectValueProp<Resource>;
  groupBy?: string;
  groups?: {
    label: string;
    value: string;
  }[];
  filter?: GridFilterModel;
  filterFn?: (
    option: SelectOption,
    index: number,
    options: SelectOption[],
  ) => boolean;
  sort?: GridSortModel;
  disablePagination?: boolean;
  processOptions?: (options: SelectOption[]) => SelectOption[];
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Resource>,
      DefaultError,
      ValidGetListResponse<Resource>,
      any
    >,
    "queryKey" | "queryFn"
  >;
};

export const useSelect = <
  Resource extends GridValidRowModel = GridValidRowModel,
>(
  props: UseSelectProps<Resource>,
) => {
  const {
    resource,
    labelProp,
    processOptions,
    valueProp,
    disablePagination = false,
    useSimplePagination = false,
    filter: filterProp,
    sort: sortProp,
    searchField,
    onQueryFn,
    groups,
    filterFn,
    groupBy,
    search,
    locale,
    include,
    queryOptions,
  } = props;
  const filter = getSelectFilter({
    labelProp,
    search,
    searchField,
    filter: filterProp,
  });
  const sort = getSelectSort({
    sort: sortProp,
    resource,
  });

  const query = useList<Resource>({
    resource: resource,
    include,
    locale,
    disablePagination: disablePagination,
    sort,
    onQueryFn,
    useSimplePagination: useSimplePagination,
    filter: filter,
    queryOptions: {
      refetchOnMount: true,
      staleTime: search ? 0 : Infinity,
      ...queryOptions,
    },
  });

  const { data, refetch } = query;

  const resources = data?.data || [];

  const options = useSelectOptions({
    valueProp,
    labelProp,
    resources,
    groupBy,
    groups,
  });

  const fetchOnOpen = (open: boolean) => {
    if (open) {
      refetch();
    }
  };

  const getResourceByValue = (value: any) => {
    return getResourceBySelectValue({
      resources,
      value,
      valueProp,
    });
  };

  const processedOptions = processOptions ? processOptions(options) : options;

  const filteredOptions = filterFn
    ? processedOptions.filter(filterFn)
    : processedOptions;

  return {
    options: filteredOptions,
    query,
    getResourceByValue,
    fetchOnOpen,
    resources,
  };
};
